import React, { useState } from 'react';
import axios from 'axios';
import { Card, Button, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import KpiCard from '../ui/KpiCard';
import { ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend, BarChart, Bar } from 'recharts';

const backendUrl = 'http://localhost:3001/api';

const CreditBureau: React.FC = () => {
  const [email, setEmail] = useState('');
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const searchCreditReport = async () => {
    if (!email) return;
    setLoading(true);
    try {
      const response = await axios.get(`${backendUrl}/bureau/credit-report/${email}`);
      setReport(response.data);
    } catch (error) {
      console.error('Error fetching credit report:', error);
      setReport(null);
    }
    setLoading(false);
  };

  const exportReport = () => {
    if (!report) return;
    const reportData = {
      applicant: report.applicantEmail,
      creditScore: report.creditScore,
      creditScoreRange: report.creditScoreRange,
      totalLoans: report.totalLoans,
      paymentReliability: report.paymentReliability,
      creditUtilization: report.creditUtilization,
      riskFactors: report.riskFactors,
      generatedAt: report.generatedAt
    };
    
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `credit-report-${email}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: 24, maxWidth: 1400 }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 32, fontWeight: 800, color: '#1e293b', marginBottom: 8 }}>Credit Bureau</h1>
        <p style={{ color: '#64748b', fontSize: 16 }}>Comprehensive credit reports, payment history, and risk assessment</p>
      </div>

      <div style={{ 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', 
        borderRadius: 16, 
        padding: 24, 
        marginBottom: 24,
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
      }}>
        <h3 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', marginBottom: 16 }}>Generate Credit Report</h3>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <TextField 
            placeholder="Enter applicant email" 
            value={email} 
            onChange={(e) => setEmail(e.target.value)}
            style={{ flex: 1 }}
            size="small"
          />
          <Button 
            variant="contained" 
            onClick={searchCreditReport}
            disabled={!email || loading}
            style={{
              background: 'linear-gradient(135deg, #1976d2, #1565c0)',
              padding: '8px 24px'
            }}
          >
            {loading ? 'Loading...' : 'Generate Report'}
          </Button>
        </div>
      </div>

      {report && (
        <>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
            <KpiCard 
              title="Credit Score" 
              value={report.creditScore} 
              subtitle={report.creditScoreRange}
              color={report.creditScore >= 750 ? '#10b981' : report.creditScore >= 650 ? '#f59e0b' : '#ef4444'} 
            />
            <KpiCard 
              title="Payment Reliability" 
              value={`${report.paymentReliability}%`} 
              color="#6366f1" 
            />
            <KpiCard 
              title="Total Loans" 
              value={report.totalLoans} 
              color="#8b5cf6" 
            />
            <KpiCard 
              title="Credit Utilization" 
              value={`${report.creditUtilization}%`} 
              color={report.creditUtilization > 80 ? '#ef4444' : '#10b981'} 
            />
          </div>

          <div style={{ display: 'flex', gap: 24, marginBottom: 24, flexWrap: 'wrap' }}>
            <div style={{ 
              flex: '1 1 500px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', 
              borderRadius: 16, 
              padding: 24,
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', margin: 0 }}>Credit Summary</h3>
                <Button variant="outlined" size="small" onClick={exportReport}>
                  Export Report
                </Button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#64748b' }}>Applicant Email:</span>
                  <span style={{ fontWeight: 600 }}>{report.applicantEmail}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#64748b' }}>Total Borrowed:</span>
                  <span style={{ fontWeight: 600 }}>{report.totalBorrowed.toLocaleString('en-ZW')} ZiG</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#64748b' }}>Total Repaid:</span>
                  <span style={{ fontWeight: 600 }}>{report.totalRepaid.toLocaleString('en-ZW')} ZiG</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#64748b' }}>On-time Payments:</span>
                  <span style={{ fontWeight: 600, color: '#10b981' }}>{report.onTimePayments}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#64748b' }}>Overdue Payments:</span>
                  <span style={{ fontWeight: 600, color: '#ef4444' }}>{report.overduePayments}</span>
                </div>
              </div>
            </div>

            <div style={{ 
              flex: '1 1 300px',
              background: 'linear-gradient(135deg, #ffffff 0%, #fef7f0 100%)', 
              borderRadius: 16, 
              padding: 24,
              border: '1px solid #fed7aa',
              boxShadow: '0 4px 20px rgba(251, 146, 60, 0.1)'
            }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', marginBottom: 16 }}>Risk Factors</h3>
              {report.riskFactors.length === 0 ? (
                <div style={{ 
                  background: 'linear-gradient(135deg, #f0fdf4, #ffffff)',
                  border: '1px solid #bbf7d0',
                  borderRadius: 8,
                  padding: 16,
                  textAlign: 'center',
                  color: '#15803d'
                }}>
                  ✅ No risk factors identified
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {report.riskFactors.map((factor: string, index: number) => (
                    <div key={index} style={{
                      background: 'linear-gradient(135deg, #fef2f2, #ffffff)',
                      border: '1px solid #fecaca',
                      borderRadius: 8,
                      padding: 12,
                      color: '#dc2626',
                      fontSize: 14,
                      fontWeight: 500
                    }}>
                      ⚠️ {factor}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div style={{ 
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', 
            borderRadius: 16, 
            padding: 24, 
            marginBottom: 24,
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', marginBottom: 16 }}>Repayment History</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ textAlign: 'left', padding: 12, fontWeight: 700, color: '#374151' }}>Due Date</th>
                    <th style={{ textAlign: 'left', padding: 12, fontWeight: 700, color: '#374151' }}>Payment Date</th>
                    <th style={{ textAlign: 'left', padding: 12, fontWeight: 700, color: '#374151' }}>Amount</th>
                    <th style={{ textAlign: 'left', padding: 12, fontWeight: 700, color: '#374151' }}>Status</th>
                    <th style={{ textAlign: 'left', padding: 12, fontWeight: 700, color: '#374151' }}>Days Late</th>
                  </tr>
                </thead>
                <tbody>
                  {report.repaymentHistory.map((payment: any) => {
                    const daysLate = payment.paymentDate && payment.dueDate ? 
                      Math.max(0, Math.floor((new Date(payment.paymentDate).getTime() - new Date(payment.dueDate).getTime()) / (1000*60*60*24))) : 
                      payment.status === 'overdue' ? Math.floor((new Date().getTime() - new Date(payment.dueDate).getTime()) / (1000*60*60*24)) : 0;
                    
                    return (
                      <tr key={payment.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: 12 }}>{new Date(payment.dueDate).toLocaleDateString()}</td>
                        <td style={{ padding: 12 }}>{payment.paymentDate ? new Date(payment.paymentDate).toLocaleDateString() : '—'}</td>
                        <td style={{ padding: 12 }}>{payment.amount.toLocaleString('en-ZW')} ZiG</td>
                        <td style={{ padding: 12 }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: 6,
                            fontSize: 12,
                            fontWeight: 600,
                            background: payment.status === 'completed' ? '#dcfce7' : '#fef2f2',
                            color: payment.status === 'completed' ? '#16a34a' : '#dc2626'
                          }}>
                            {payment.status.toUpperCase()}
                          </span>
                        </td>
                        <td style={{ padding: 12, color: daysLate > 0 ? '#dc2626' : '#6b7280' }}>
                          {daysLate > 0 ? `${daysLate} days` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ 
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', 
            borderRadius: 16, 
            padding: 24,
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', marginBottom: 16 }}>Loan Applications History</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {report.applications.map((app: any) => (
                <div key={app.id} style={{
                  background: 'linear-gradient(135deg, #fafafa, #ffffff)',
                  border: '1px solid #e5e7eb',
                  borderRadius: 12,
                  padding: 16,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                      {app.requestedAmount.toLocaleString('en-ZW')} ZiG • Score: {app.creditScore}
                    </div>
                    <div style={{ fontSize: 14, color: '#6b7280' }}>
                      {new Date(app.submissionDate).toLocaleDateString()} • {app.customerType}
                    </div>
                  </div>
                  <div style={{
                    padding: '6px 12px',
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 600,
                    background: app.status === 'approved' ? '#dcfce7' : app.status === 'rejected' ? '#fef2f2' : '#fef3c7',
                    color: app.status === 'approved' ? '#16a34a' : app.status === 'rejected' ? '#dc2626' : '#d97706'
                  }}>
                    {app.status.toUpperCase()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CreditBureau;
